import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const PHONE = "6287781058814"; // OPIKJEWELS concierge WhatsApp

/**
 * Floating WhatsApp call-to-action — visible on every page.
 *
 *   - Pre-fills a contextual message based on the current path
 *     (gemstone page → ask about the stone, auction → live floor, etc.)
 *   - Opens wa.me in a new tab so the chat continues in the buyer's WhatsApp
 *   - Pulsing brand-green ring + subtle entrance animation
 *   - Hidden inside `/admin` routes so it never overlaps admin UI
 */
const WhatsAppFab = () => {
  const { pathname } = useLocation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Entrance fade-in after 600 ms so it doesn't compete with the hero
    const t = setTimeout(() => setMounted(true), 600);
    return () => clearTimeout(t);
  }, []);

  // Hide in the admin console — concierge isn't relevant for staff workflows
  if (pathname.includes("/admin")) return null;

  // Contextual pre-fill — keeps the convo focused right from message #1
  const messageFor = (path) => {
    if (path.includes("/gemstone/")) return "Hello OPIKJEWELS, I have a question about this gemstone:";
    if (path.includes("/auction"))   return "Hello OPIKJEWELS, I would like to register for the live auction:";
    if (path.includes("/verify"))    return "Hello OPIKJEWELS, I need help verifying a certificate:";
    if (path.includes("/passport"))  return "Hello OPIKJEWELS, I would like to discuss my Gem Passport:";
    if (path.includes("/contact"))   return "Hello OPIKJEWELS, I would like to arrange a private viewing:";
    return "Hello OPIKJEWELS concierge, I would like to learn more.";
  };

  const url = `https://wa.me/${PHONE}?text=${encodeURIComponent(
    `${messageFor(pathname)} (Page: ${typeof window !== "undefined" ? window.location.href : ""})`
  )}`;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-fab"
      aria-label="Chat on WhatsApp"
      className={`group fixed bottom-6 right-6 z-[60] flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] hover:bg-[#1ebe57] shadow-2xl transition-all duration-500 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ boxShadow: "0 10px 30px -8px rgba(37,211,102,.55), 0 4px 12px rgba(0,0,0,.35)" }}
    >
      {/* Pulsing brand-green ring — pure CSS, no JS animation cost */}
      <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-50 animate-ping pointer-events-none" />
      {/* Official WhatsApp glyph — Lucide doesn't ship one, so we inline the SVG */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        className="relative w-7 h-7 md:w-8 md:h-8 text-white"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M16.003 3C9.376 3 4 8.376 4 15.003c0 2.276.612 4.494 1.78 6.444L4 28l6.71-1.748a12.013 12.013 0 0 0 5.294 1.245h.005C22.624 27.497 28 22.121 28 15.494 28 12.286 26.756 9.276 24.5 7.02A11.94 11.94 0 0 0 16.003 3Zm0 21.836h-.005a10.022 10.022 0 0 1-5.105-1.4l-.366-.217-3.98 1.037 1.06-3.88-.238-.398a9.96 9.96 0 0 1-1.53-5.336c0-5.523 4.498-10.021 10.025-10.021a9.96 9.96 0 0 1 7.087 2.937 9.96 9.96 0 0 1 2.93 7.09c0 5.524-4.498 10.188-10.023 10.188Zm5.51-7.498c-.302-.151-1.787-.882-2.064-.983-.277-.1-.479-.151-.681.151-.202.302-.781.983-.958 1.184-.177.202-.353.227-.655.075-.302-.15-1.273-.469-2.424-1.496-.896-.799-1.5-1.785-1.677-2.087-.176-.302-.019-.465.132-.616.135-.135.302-.353.453-.529.15-.177.201-.302.302-.504.1-.202.05-.378-.025-.529-.076-.151-.681-1.643-.933-2.249-.246-.59-.495-.51-.681-.519l-.58-.011a1.116 1.116 0 0 0-.808.378c-.277.302-1.06 1.036-1.06 2.527s1.085 2.933 1.236 3.135c.151.202 2.135 3.26 5.173 4.572.723.312 1.286.498 1.726.638.725.231 1.385.198 1.906.12.582-.087 1.787-.731 2.04-1.437.252-.706.252-1.31.176-1.437-.076-.126-.277-.202-.58-.353Z"/>
      </svg>
      <span className="sr-only">Chat on WhatsApp</span>
    </a>
  );
};

export default WhatsAppFab;
